import { ref, watch } from "vue";

/**
 * Mémorise par ligue le résultat d'un chargement asynchrone, et expose son
 * état de chargement
 * Comportements garantis : mémorisation par ligue (une ligue déjà vue n'est
 * jamais rechargée), remise à faux de isLoading sur le chemin « déjà en
 * cache », mise en cache des seuls succès (un échec reste réessayable),
 * garde contre une réponse périmée (la ligue affichée a pu changer pendant
 * le chargement), et trace de l'erreur dans la console en français.
 * @param {import("vue").Ref<Object>} league la ligue réactive à charger, exposant leagueId et name
 * @param {Function} loader la fonction de chargement propre à l'appelant, appelée avec la ligue
 * @param {String} label ce que charge l'appelant, pour distinguer les traces d'erreur des différents consommateurs
 * @returns {{ isLoading: import("vue").Ref<Boolean>, value: import("vue").Ref }} l'état de chargement et la valeur chargée
 */
const useLeagueCache = (league, loader, label) => {
  const isLoading = ref(false);
  // Valeurs déjà chargées, indexées par ligue
  const valueByLeagueId = new Map();
  const value = ref(undefined);

  const loadLeague = async (currentLeague) => {
    const { leagueId } = currentLeague;

    if (valueByLeagueId.has(leagueId)) {
      value.value = valueByLeagueId.get(leagueId);
      isLoading.value = false;
      return;
    }

    isLoading.value = true;

    // Une ligue en échec n'est pas mise en cache pour rester réessayable
    // au prochain passage sur la ligue
    let loadedValue;
    try {
      loadedValue = await loader(currentLeague);
      valueByLeagueId.set(leagueId, loadedValue);
    } catch (err) {
      console.error(`Erreur lors du chargement (${label}) pour la ligue ${currentLeague.name} :`, err);
      loadedValue = undefined;
    }

    // La ligue affichée a pu changer pendant le chargement : on n'écrase
    // la valeur que si la réponse correspond toujours à la ligue demandée
    if (league.value.leagueId === leagueId) {
      value.value = loadedValue;
      isLoading.value = false;
    }
  };

  watch(league, loadLeague, { immediate: true });

  return {
    isLoading,
    value,
  };
};

export { useLeagueCache };